import apiClient from './apiClient';

interface GenerateReportParams {
  type: string;
  start_date: string;
  end_date: string;
  [key: string]: any;
}

/**
 * レポートサービス
 */
class ReportService {
  /**
   * 汎用レポート生成
   */
  static async generateReport(params: GenerateReportParams) {
    try {
      console.log('汎用レポートを生成中...', params);
      const response = await apiClient.post('/reports/generate', params);
      console.log('汎用レポート生成成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('レポート生成に失敗しました:', error);
      
      // エラー時はモックデータを返す
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
          error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.warn('⚠️ ネットワークエラーのため、モックレポートを生成します');
        return {
          success: true,
          report_id: `mock_${Date.now()}`,
          type: params.type,
          status: 'completed',
          message: 'モックレポートが生成されました'
        };
      }
      throw error;
    }
  }

  /**
   * 売上レポート取得
   * @param startDate 開始日 (YYYY-MM-DD)
   * @param endDate 終了日 (YYYY-MM-DD)
   * @param period 期間タイプ ('daily', 'weekly', 'monthly')
   */
  static async getSalesReport(startDate: string, endDate: string, period: string = 'daily') {
    try {
      console.log('売上レポートを取得中...', { startDate, endDate, period });
      
      // period は 'day' / 'week' / 'month'（Reports.tsx の groupBy）、
      // もしくは 'daily' / 'weekly' / 'monthly' のどちらでも受け付ける。
      const normalizedGroupBy =
        period === 'daily' || period === 'day'
          ? 'day'
          : period === 'weekly' || period === 'week'
          ? 'week'
          : 'month';

      const response = await apiClient.get('/reports/sales', {
        params: {
          start_date: startDate,
          end_date: endDate,
          group_by: normalizedGroupBy
        }
      });
      
      // レスポンスの形式を統一
      const data = response.data;
      
      // レポートタイプをフォーマットに合わせて設定
      if (data && data.report) {
        data.report.report_type = period;
      }
      
      // バックエンドが cast_revenue / store_revenue / grand_total / summary を
      // 現行スキーマで返すため、フロントでの再計算は行わずそのまま使用する。
      // 万一 summary が無い場合のみ data から最低限の集計を補完する。
      if (data && Array.isArray(data.data) && !data.summary) {
        data.summary = {
          total_revenue: data.data.reduce((sum, item) => sum + (item.revenue || 0), 0),
          total_grand_total: data.data.reduce((sum, item) => sum + (item.grand_total || 0), 0),
          cast_revenue: data.data.reduce((sum, item) => sum + (item.cast_revenue || 0), 0),
          store_revenue: data.data.reduce((sum, item) => sum + (item.store_revenue || 0), 0),
        };
      }
      
      console.log('売上レポート取得成功:', data);
      return data;
    } catch (error: any) {
      console.error('売上レポート取得エラー:', error);
      
      // エラー時はモックデータを返す
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
          error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.warn('⚠️ ネットワークエラーのため、モック売上レポートを返します');
        return getMockSalesReport(startDate, endDate, period);
      }
      throw error;
    }
  }

  /**
   * 注文レポート取得
   * @param startDate 開始日 (YYYY-MM-DD)
   * @param endDate 終了日 (YYYY-MM-DD)
   * @param groupBy グループ化 ('day', 'week', 'month') - オプション
   */
  static async getOrderReport(startDate: string, endDate: string, groupBy: string = 'day') {
    try {
      console.log('注文レポートを取得中...', { startDate, endDate, groupBy });
      
      const response = await apiClient.get('/reports/orders', {
        params: {
          start_date: startDate,
          end_date: endDate,
          group_by: groupBy
        }
      });
      
      // レスポンスの形式を統一
      const data = response.data;
      
      // レポートタイプをフォーマットに合わせて設定
      if (data && data.report) {
        data.report.report_type = 'order';
      }
      
      console.log('注文レポート取得成功:', data);
      return data;
    } catch (error: any) {
      console.error('注文レポート取得エラー:', error);
      
      // エラー時はモックデータを返す
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
          error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.warn('⚠️ ネットワークエラーのため、モック注文レポートを返します');
        return getMockOrderReport(startDate, endDate, groupBy);
      }
      throw error;
    }
  }

  /**
   * スタッフパフォーマンスレポート取得
   * @param startDate 開始日 (YYYY-MM-DD)
   * @param endDate 終了日 (YYYY-MM-DD)
   */
  static async getStaffReport(startDate: string, endDate: string) {
    try {
      console.log('スタッフレポートを取得中...', { startDate, endDate });
      
      const response = await apiClient.get('/reports/staff', {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      
      // レスポンスの形式を統一
      const data = response.data;
      
      // レポートタイプをフォーマットに合わせて設定
      if (data && data.report) {
        data.report.report_type = 'performance';
      }
      
      console.log('スタッフレポート取得成功:', data);
      return data;
    } catch (error: any) {
      console.error('スタッフレポート取得エラー:', error);
      
      // エラー時はモックデータを返す
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
          error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.warn('⚠️ ネットワークエラーのため、モックスタッフレポートを返します');
        return getMockStaffReport(startDate, endDate);
      }
      throw error;
    }
  }

  /**
   * GPS追跡レポート取得
   * @param startDate 開始日 (YYYY-MM-DD)
   * @param endDate 終了日 (YYYY-MM-DD)
   */
  static async getGpsReport(startDate: string, endDate: string) {
    try {
      console.log('GPSレポートを取得中...', { startDate, endDate });
      
      const response = await apiClient.get('/reports/gps', {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      
      // レスポンスの形式を統一
      const data = response.data;
      
      // レポートタイプをフォーマットに合わせて設定
      if (data && data.report) {
        data.report.report_type = 'gps';
      }
      
      console.log('GPSレポート取得成功:', data);
      return data;
    } catch (error: any) {
      console.error('GPS追跡レポート取得エラー:', error);
      
      // エラー時はモックデータを返す
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
          error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.warn('⚠️ ネットワークエラーのため、モックGPSレポートを返します');
        return getMockGpsReport(startDate, endDate);
      }
      throw error;
    }
  }

  /**
   * キャストパフォーマンスレポート取得
   * @param startDate 開始日 (YYYY-MM-DD)
   * @param endDate 終了日 (YYYY-MM-DD)
   */
  static async getCastPerformance(startDate: string, endDate: string) {
    try {
      console.log('キャストパフォーマンスレポートを取得中...', { startDate, endDate });
      
      const response = await apiClient.get('/reports/cast-performance', {
        params: { start_date: startDate, end_date: endDate },
      });
      
      console.log('キャストパフォーマンスレポート取得成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('キャストパフォーマンスレポート取得エラー:', error);
      
      // エラー時はモックデータを返す
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
          error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.warn('⚠️ ネットワークエラーのため、モックキャストパフォーマンスレポートを返します');
        return getMockCastPerformanceReport(startDate, endDate);
      }
      throw error;
    }
  }

  /**
   * キャスト別オーダー明細取得（日別詳細・日別合計バック）
   * @param castId キャストのユーザーID
   * @param startDate 開始日 (YYYY-MM-DD)
   * @param endDate 終了日 (YYYY-MM-DD)
   */
  static async getCastDetail(castId: number, startDate: string, endDate: string) {
    try {
      console.log('キャスト明細レポートを取得中...', { castId, startDate, endDate });

      const response = await apiClient.get('/reports/cast-detail', {
        params: { cast_id: castId, start_date: startDate, end_date: endDate },
      });

      console.log('キャスト明細レポート取得成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('キャスト明細レポート取得エラー:', error);
      throw error;
    }
  }

  /**
   * ドライバー一覧取得（出勤履歴レポートの名前検索・選択用）
   */
  static async getDrivers() {
    try {
      const response = await apiClient.get('/reports/drivers');
      return response.data; // { success, drivers: [{driver_id, driver_name}] }
    } catch (error: any) {
      console.error('ドライバー一覧取得エラー:', error);
      throw error;
    }
  }

  /**
   * ドライバー出勤履歴（位置共有オン/オフ）レポート取得
   * @param driverId ドライバーID
   * @param startDate 開始日 (YYYY-MM-DD)
   * @param endDate 終了日 (YYYY-MM-DD)
   */
  static async getDriverAttendance(driverId: number, startDate: string, endDate: string) {
    try {
      const response = await apiClient.get('/reports/driver-attendance', {
        params: { driver_id: driverId, start_date: startDate, end_date: endDate },
      });
      return response.data; // { success, driver_id, driver_name, history, daily, summary }
    } catch (error: any) {
      console.error('ドライバー出勤履歴取得エラー:', error);
      throw error;
    }
  }

  /**
   * ドライバーパフォーマンスレポート取得
   * @param startDate 開始日 (YYYY-MM-DD)
   * @param endDate 終了日 (YYYY-MM-DD)
   */
  static async getDriverPerformance(startDate: string, endDate: string) {
    try {
      console.log('ドライバーパフォーマンスレポートを取得中...', { startDate, endDate });
      
      const response = await apiClient.get('/reports/driver-performance', {
        params: { start_date: startDate, end_date: endDate },
      });
      
      console.log('ドライバーパフォーマンスレポート取得成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('ドライバーパフォーマンスレポート取得エラー:', error);
      
      // エラー時はモックデータを返す
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
          error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.warn('⚠️ ネットワークエラーのため、モックドライバーパフォーマンスレポートを返します');
        return getMockDriverPerformanceReport(startDate, endDate);
      }
      throw error;
    }
  }

  /**
   * 顧客分析レポート取得
   * @param startDate 開始日 (YYYY-MM-DD)
   * @param endDate 終了日 (YYYY-MM-DD)
   */
  static async getCustomerAnalytics(startDate: string, endDate: string) {
    try {
      console.log('顧客分析レポートを取得中...', { startDate, endDate });
      
      const response = await apiClient.get('/reports/customer-analytics', {
        params: { start_date: startDate, end_date: endDate },
      });
      
      console.log('顧客分析レポート取得成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('顧客分析レポート取得エラー:', error);
      
      // エラー時はモックデータを返す
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
          error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.warn('⚠️ ネットワークエラーのため、モック顧客分析レポートを返します');
        return getMockCustomerAnalyticsReport(startDate, endDate);
      }
      throw error;
    }
  }

  /**
   * レポートダウンロード
   * @param reportId レポートID
   */
  static async downloadReport(reportId: string) {
    try {
      console.log('レポートをダウンロード中...', reportId);
      
      const response = await apiClient.get(`/reports/download/${reportId}`, {
        responseType: 'blob'
      });
      
      console.log('レポートダウンロード成功');
      return response.data;
    } catch (error: any) {
      console.error('レポートダウンロードエラー:', error);
      
      // エラー時はモックCSVを返す
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
          error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.warn('⚠️ ネットワークエラーのため、モックCSVデータを返します');
        
        const mockCsv = `日付,売上,注文数,キャスト収益,店舗収益
${new Date().toISOString().split('T')[0]},150000,12,45000,105000
${new Date(Date.now() - 86400000).toISOString().split('T')[0]},120000,8,36000,84000`;
        
        return new Blob([mockCsv], { type: 'text/csv;charset=utf-8;' });
      }
      throw error;
    }
  }

  /**
   * レポートエクスポート
   * @param reportType レポートタイプ
   * @param startDate 開始日
   * @param endDate 終了日
   * @param format フォーマット
   */
  static async exportReport(reportType: string, startDate: string, endDate: string, format: string = 'excel') {
    try {
      console.log('レポートをエクスポート中...', { reportType, startDate, endDate, format });
      
      const response = await apiClient.get('/reports/export', {
        params: {
          type: reportType,
          start_date: startDate,
          end_date: endDate,
          format: format
        },
        responseType: 'blob'
      });
      
      console.log('レポートエクスポート成功');
      return response.data;
    } catch (error: any) {
      console.error('レポートエクスポートエラー:', error);
      
      // エラー時はモックファイルを返す
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
          error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.warn('⚠️ ネットワークエラーのため、モックファイルを返します');
        
        const mockData = format === 'excel' ? 
          new Blob(['Mock Excel Data'], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }) :
          new Blob(['Mock CSV Data'], { type: 'text/csv;charset=utf-8;' });
        
        return mockData;
      }
      throw error;
    }
  }
}

// モックデータ生成関数群
const getMockSalesReport = (startDate: string, endDate: string, period: string) => {
  return {
    report: {
      report_type: period,
      start_date: startDate,
      end_date: endDate
    },
    data: [
      {
        date: startDate,
        revenue: 150000,
        cast_revenue: 45000,
        store_revenue: 105000,
        orders: 12
      },
      {
        date: endDate,
        revenue: 120000,
        cast_revenue: 36000,
        store_revenue: 84000,
        orders: 8
      }
    ],
    summary: {
      total_revenue: 270000,
      cast_revenue: 81000,
      store_revenue: 189000
    }
  };
};

const getMockOrderReport = (startDate: string, endDate: string, groupBy: string) => {
  return {
    report: {
      report_type: 'order',
      start_date: startDate,
      end_date: endDate
    },
    data: [
      {
        date: startDate,
        total_orders: 12,
        completed_orders: 10,
        cancelled_orders: 2,
        average_order_value: 12500
      }
    ]
  };
};

const getMockStaffReport = (startDate: string, endDate: string) => {
  return {
    report: {
      report_type: 'performance',
      start_date: startDate,
      end_date: endDate
    },
    data: [
      {
        staff_name: '鈴木 一郎',
        total_orders: 25,
        revenue_generated: 312500,
        rating: 4.8
      },
      {
        staff_name: '田中 三郎',
        total_orders: 18,
        revenue_generated: 225000,
        rating: 4.6
      }
    ]
  };
};

const getMockGpsReport = (startDate: string, endDate: string) => {
  return {
    report: {
      report_type: 'gps',
      start_date: startDate,
      end_date: endDate
    },
    data: [
      {
        driver_name: '鈴木 一郎',
        total_distance: 245.6,
        total_time: 480,
        average_speed: 30.7
      }
    ]
  };
};

const getMockCastPerformanceReport = (startDate: string, endDate: string) => {
  return {
    data: [
      {
        cast_name: '山田 花子',
        total_bookings: 15,
        revenue: 187500,
        rating: 4.9
      }
    ]
  };
};

const getMockDriverPerformanceReport = (startDate: string, endDate: string) => {
  return {
    data: [
      {
        driver_name: '鈴木 一郎',
        total_trips: 32,
        distance: 456.7,
        rating: 4.8
      }
    ]
  };
};

const getMockCustomerAnalyticsReport = (startDate: string, endDate: string) => {
  return {
    data: [
      {
        customer_segment: 'VIP',
        count: 25,
        revenue: 625000,
        average_order: 25000
      }
    ]
  };
};

export default ReportService;
